import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DateRangeFilterProps {
  /** Human label for the range, e.g. "Created" or "Closing". */
  label: string;
  /** Stable id prefix so the two inputs get unique, associated labels (a11y). */
  idPrefix: string;
  /** Raw `<input type=date>` values (`YYYY-MM-DD`) or empty string. */
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  /** Hide the quarter/month quick-pick dropdown (the raw inputs stay). */
  hidePresets?: boolean;
  /** Set both ends in one call when a preset is picked. Callers whose
   *  `onFromChange`/`onToChange` close over the current range need this — two
   *  separate calls would let the second write back the stale other end. */
  onRangeChange?: (from: string, to: string) => void;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

const pad = (n: number) => String(n).padStart(2, '0');
/** Last calendar day of `month1` (1-based) in `year`. */
const lastDay = (year: number, month1: number) => new Date(year, month1, 0).getDate();

/** Inclusive `[from, to]` for quarter `q` (1–4) of `year`, as `YYYY-MM-DD`. */
export function quarterRange(year: number, q: number): { from: string; to: string } {
  const startMonth = (q - 1) * 3 + 1; // Q1→1, Q2→4, Q3→7, Q4→10
  const endMonth = startMonth + 2;
  return {
    from: `${year}-${pad(startMonth)}-01`,
    to: `${year}-${pad(endMonth)}-${pad(lastDay(year, endMonth))}`,
  };
}

/** Inclusive `[from, to]` for month `m` (1–12) of `year`, as `YYYY-MM-DD`. */
export function monthRange(year: number, m: number): { from: string; to: string } {
  return { from: `${year}-${pad(m)}-01`, to: `${year}-${pad(m)}-${pad(lastDay(year, m))}` };
}

/** Which preset (if any) the current `[from, to]` exactly matches, for the given
 *  year — so the dropdown reflects a chosen quarter/month, else shows "Custom". */
function matchPreset(year: number, from: string, to: string): string {
  if (!from && !to) return 'any';
  for (let q = 1; q <= 4; q += 1) {
    const r = quarterRange(year, q);
    if (r.from === from && r.to === to) return `q${q}`;
  }
  for (let m = 1; m <= 12; m += 1) {
    const r = monthRange(year, m);
    if (r.from === from && r.to === to) return `m${m}`;
  }
  return 'custom';
}

/** Two associated date inputs forming an inclusive `[from, to]` window, with a
 *  quarter/month quick-pick that fills the range. Controlled — the parent owns
 *  the values and maps them to query params. Each input carries an `aria-label`. */
export function DateRangeFilter({
  label,
  idPrefix,
  from,
  to,
  onFromChange,
  onToChange,
  hidePresets = false,
  onRangeChange,
}: DateRangeFilterProps) {
  const year = new Date().getFullYear();
  const selected = matchPreset(year, from, to);

  function setRange(nextFrom: string, nextTo: string) {
    if (onRangeChange) {
      onRangeChange(nextFrom, nextTo);
      return;
    }
    onFromChange(nextFrom);
    onToChange(nextTo);
  }

  function applyPreset(value: string) {
    if (value === 'any') {
      setRange('', '');
      return;
    }
    if (value === 'custom') return; // display-only — keep the manual range
    const range = value.startsWith('q')
      ? quarterRange(year, Number(value.slice(1)))
      : monthRange(year, Number(value.slice(1)));
    setRange(range.from, range.to);
  }

  return (
    <div className="flex items-center gap-1.5">
      {/* Label kept for screen readers; the period/date inputs already derive
          their visible context from their own values + aria-labels. */}
      <span className="sr-only">{label}</span>
      {hidePresets ? null : (
        <Select value={selected} onValueChange={applyPreset}>
          <SelectTrigger className="h-9 w-28" aria-label={`${label} period`}>
            <SelectValue placeholder="Period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any time</SelectItem>
            {/* Shown only when the manual range matches no preset. */}
            {selected === 'custom' ? (
              <SelectItem value="custom" disabled>
                Custom range
              </SelectItem>
            ) : null}
            <SelectGroup>
              <SelectLabel>Quarters ({year})</SelectLabel>
              {[1, 2, 3, 4].map((q) => (
                <SelectItem key={`q${q}`} value={`q${q}`}>
                  Q{q} {year}
                </SelectItem>
              ))}
            </SelectGroup>
            <SelectGroup>
              <SelectLabel>Months ({year})</SelectLabel>
              {MONTHS.map((name, i) => (
                <SelectItem key={`m${i + 1}`} value={`m${i + 1}`}>
                  {name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      )}
      <Input
        id={`${idPrefix}-from`}
        type="date"
        aria-label={`${label} from`}
        value={from}
        max={to || undefined}
        onChange={(e) => onFromChange(e.target.value)}
        className="h-9 w-36"
      />
      <span className="text-muted-foreground text-xs" aria-hidden>
        –
      </span>
      <Input
        id={`${idPrefix}-to`}
        type="date"
        aria-label={`${label} to`}
        value={to}
        min={from || undefined}
        onChange={(e) => onToChange(e.target.value)}
        className="h-9 w-36"
      />
    </div>
  );
}

/** Expand a `YYYY-MM-DD` input to the last instant of that day so a `*_before`
 *  bound on a DateTime field (created / occurred_at) is inclusive of the day.
 *  Returns undefined for empty input. DateField filters take the date as-is. */
export function endOfDayParam(dateInput: string): string | undefined {
  return dateInput ? `${dateInput}T23:59:59` : undefined;
}
